import { FlatfileRecord } from '@flatfile/hooks';
import { FIELD_TYPE, submitAction } from 'components/imports/blueprintUtils';
import { MAX_STR_LENGTH, base, nullable, required } from 'components/imports/schemaUtils';
import { ImportStrategy } from 'components/imports/types';
import { yup } from 'helpers/yup';
import { ValidationError } from 'yup';

const softCreditSchema = yup.object().shape({
    transaction_number: required.label('Transaction Reference Number').max(10),
    contact_id: nullable.label('Contact ID').asNumber(schema => schema.nullable().positive()),
    first_name: base
        .label('First Name')
        .max(MAX_STR_LENGTH)
        .when('$hasContactId', ([hasContactId], schema) =>
            hasContactId
                ? schema.nullable()
                : schema.required('Must provide First Name when no Contact ID is provided.'),
        ),
    last_name: base
        .label('Last Name')
        .max(MAX_STR_LENGTH)
        .when('$hasContactId', ([hasContactId], schema) =>
            hasContactId
                ? schema.nullable()
                : schema.required('Must provide Last Name when no Contact ID is provided.'),
        ),
    email: nullable.label('Email').email(),
    amount: required.label('Amount').asNumber(schema => schema.positive()),
    note: nullable.max(MAX_STR_LENGTH).label('Note'),
});

const useSoftCreditImportStrategy = (): ImportStrategy => {
    return {
        displayName: 'soft credits',
        type: 'soft_credits',
        hasDownloadTemplate: false,
        blueprint: {
            name: 'Import soft credits',
            sheets: [
                {
                    name: 'Soft credits',
                    slug: 'soft_credits',
                    fields: [
                        {
                            label: 'Transaction Reference Number',
                            key: 'transaction_number',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Contact ID',
                            key: 'contact_id',
                            type: FIELD_TYPE.number,
                        },
                        {
                            label: 'First Name',
                            key: 'first_name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Last Name',
                            key: 'last_name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Email',
                            key: 'email',
                            type: FIELD_TYPE.string,
                        },

                        {
                            label: 'Amount ($)',
                            key: 'amount',
                            type: FIELD_TYPE.number,
                        },
                        {
                            label: 'Note',
                            key: 'note',
                            type: FIELD_TYPE.string,
                        },
                    ],
                },
            ],
            actions: [submitAction],
        },
        recordHook: (record: FlatfileRecord) => {
            const data = record.value;

            const context = {
                hasContactId: data.contact_id !== null,
            };

            try {
                softCreditSchema.validateSync(data, { context, abortEarly: false });
            } catch (err) {
                if (!(err instanceof ValidationError)) {
                    return record;
                }

                if (!err.inner) {
                    record.addError(err.path!, err.message);

                    return record;
                }

                err.inner.forEach((e: any) => {
                    record.addError(e.path, e.message);
                });
            }

            return record;
        },
    };
};

export { useSoftCreditImportStrategy };
